import { authenticationStore } from "@/state/auth/AuthStore";
import { resetTransaction } from "@/state/transactions/CurrentTransactionEvents";
import { MultipleTransactionActionResponse } from "@/state/transactions/response/MultipleTransactionActionResponse";
import {
  requestPostExportDasbhoard,
  requestPostMultipleTransaction,
} from "@/state/transactions/TransactionEffects";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import {
  MultipleTransactionAction,
  TransactionDetail,
  TransactionSearch,
  TransactionType,
} from "@/type/core/transaction/transaction.types";
import {
  DASHBOARD_EXPORT_FILENAME,
  PAGINATION_SIZE_ENUM,
} from "@/utils/Constant";
import { FileHelper } from "@/utils/helpers/file-helper";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import BasicButton from "@components/core/buttons/BasicButton";
import InfoModal from "@components/core/modals/InfoModal";
import BasicTable from "@components/core/tables/BasicTable";
import { ROUTES } from "@routes/Routes";
import { Pageable } from "@type/core/pagination/pagination.types";
import { columns } from "@views/core/extranet/home/columns/DashboardColumns";
import { Pagination, Select } from "antd";
import { useUnit } from "effector-react";
import {
  Dispatch,
  FunctionComponent,
  Key,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const { Option } = Select;

interface DashboardTableProps {
  transactionsLoading: boolean;
  sortedTransactions: Pageable<TransactionDetail>;
  refetchTransactions: () => void;
  className?: string;
  setLimit: Dispatch<SetStateAction<number>>;
  setPageNumber: Dispatch<SetStateAction<number>>;
  body: TransactionSearch;
}

const DashboardTable: FunctionComponent<DashboardTableProps> = ({
  transactionsLoading,
  sortedTransactions,
  refetchTransactions,
  className = "",
  setLimit,
  setPageNumber,
  body,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [action, setAction] = useState<MultipleTransactionAction>();
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  const isLoadingAny = transactionsLoading || isLoading;

  useEffect(() => {
    return requestPostMultipleTransaction.doneData.watch((response) => {
      if (response.ok && response.data) {
        success(response.data);
      } else {
        toastError(t(`errors.transactionMultipleAction.${action}`));
      }
      setIsLoading(false);
    });
  });

  useEffect(() => {
    return requestPostExportDasbhoard.done.watch(({ result }) => {
      if (result.ok && result.data) {
        FileHelper.createDownloadFileFromBlob(
          result.data,
          DASHBOARD_EXPORT_FILENAME,
          result.headers,
        );
      } else {
        t("error.dashboard.export");
      }
      setIsLoading(false);
    });
  });

  const success = (
    multipleTransactionActionResponse: MultipleTransactionActionResponse,
  ) => {
    const toast = `${t(`success.transactionsActionResult.${action}`, {
      count: multipleTransactionActionResponse.successCount,
    })}${t("success.transactionsActionResult.total", { total: multipleTransactionActionResponse.total })}`;

    if (multipleTransactionActionResponse.successCount === 0) {
      return toastError(toast);
    }
    refetchTransactions();
    return toastSuccess(toast);
  };

  const multipleTransactionAction = (action: MultipleTransactionAction) => {
    setIsLoading(true);
    setAction(action);
    void requestPostMultipleTransaction({ dto: selectedRowIds, id: action });
  };

  const manage = (
    transactionId?: string,
    type?: TransactionType,
    status?: string,
  ) => {
    if (!transactionId) {
      return;
    }

    resetTransaction();
    if (type === "scellement" && status === "inProgress") {
      navigate(ROUTES.extranet.transactions.process.generate(transactionId));
    } else {
      navigate(ROUTES.extranet.transactions.details.generate(transactionId));
    }
  };

  const handleExportDashboard = () => {
    setIsLoading(true);
    void requestPostExportDasbhoard({ dto: body });
  };

  return (
    <div className={`app-table-dashboard ${className}`}>
      {(sortedTransactions.totalElements > 0 || isLoadingAny) && (
        <BasicTable
          isLoading={isLoadingAny}
          rowSelection={{
            type: "checkbox",
            onCell: () => ({
              onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
              },
            }),
            onChange: (selectedRowKeys: Key[]) => {
              setSelectedRowIds(selectedRowKeys as string[]);
            },
          }}
          onRow={(record: TransactionDetail) => ({
            onClick: () =>
              navigate(
                ROUTES.extranet.transactions.details.generate(record.id ?? ""),
              ),
          })}
          pagination={false}
          columns={columns(
            manage,
            setIsLoading,
            refetchTransactions,
            authStore?.user,
          )}
          dataSource={sortedTransactions.content}
          rowKey="id"
          className="mb-16"
          emptyTextNull
        />
      )}
      {selectedRowIds.length > 0 && (
        <div className="dashboard-actions">
          <span>{t("home.dashboard.table.buttons.select")}</span>
          <BasicButton
            text={t("home.dashboard.table.buttons.launch")}
            onClick={() => multipleTransactionAction("launch")}
          />
          <BasicButton
            text={t("home.dashboard.table.buttons.transactionRelaunch")}
            onClick={() => multipleTransactionAction("relaunch")}
          />
          <BasicButton
            text={t("home.dashboard.table.buttons.classify")}
            onClick={() => multipleTransactionAction("classify")}
          />
          <InfoModal value="dashboardMultipleButtons" />
        </div>
      )}
      {!isLoadingAny && (
        <div className="d-flex justify-content-center mb-16">
          <h3 className="text-primary">
            {t("home.dashboard.table.numberOfTransaction", {
              count: sortedTransactions.totalElements ?? 0,
            })}
          </h3>
        </div>
      )}
      {sortedTransactions?.content &&
        sortedTransactions.content.length !== 0 && (
          <div className="dashboard-pagination">
            <div className="pagination-left">
              <div className="d-flex align-items-center">
                <Select
                  disabled={isLoading || transactionsLoading}
                  value={sortedTransactions.size}
                  onChange={(e: number) => setLimit(e)}
                >
                  {PAGINATION_SIZE_ENUM.map((option, index) => (
                    <Option key={index} value={option} label={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
                <div className="pagination-label">
                  {t("home.dashboard.table.perPage")}
                </div>
              </div>
            </div>
            <div className="pagination-center">
              <BasicButton
                variant="tertiary"
                text={t("home.dashboard.table.exportData")}
                onClick={handleExportDashboard}
              />
            </div>
            <div className="pagination-right">
              <Pagination
                showSizeChanger={false}
                pageSize={sortedTransactions.size}
                total={sortedTransactions.totalElements}
                showLessItems
                onChange={(page: number) => {
                  setPageNumber(page);
                }}
              />
            </div>
          </div>
        )}
    </div>
  );
};

export default DashboardTable;
